/**
 * Meoora Theme Configuration
 * ===========================
 * Single source of truth for all visual tokens.
 * Change colors, typography, spacing, and branding from this one file.
 *
 * These values are used in:
 *   - tailwind.config.js (via require at build time)
 *   - CSS custom properties (generated at build)
 *   - React components (imported directly)
 */

// ─── Brand ──────────────────────────────────────────────
export const brand = {
  name: "Meoora",
  tagline: "The art of natural beauty",
  currency: "INR",
  currencySymbol: "₹",
  locale: "en-IN",
  domain: "meoora.in",
} as const;

// ─── Colors ─────────────────────────────────────────────
// Nature-inspired palette. Adjust any shade freely.
export const colors = {
  // Primary greens
  green: {
    primary: "#d3e8b1",   // Soft sage — announcement bar, accents
    light: "#e1f2c5",     // Light mint — hover states, highlights
    pale: "#e0ebe1",      // Muted sage — section backgrounds (e.g. testimonials)
    dark: "#34442b",      // Deep forest — dark sections (e.g. CollectionTabs)
    darker: "#2b3823",    // Darkest green — button hovers
    accent: "#5f8f3a",    // Vivid green — badges, active indicators, links
    muted: "#a8d08d",     // Soft green — dark-bg text accents
  },
  // Neutrals
  neutral: {
    black: "#000000",
    ink: "#1a1a1a",       // Near-black for body text
    dark: "#333333",
    mid: "#494949",       // Footer bottom text
    muted: "#696969",     // Secondary text, eyebrow labels
    light: "#909090",     // Tertiary text
    line: "#e5e5e5",      // Borders, dividers
    border: "#dcdcdc",    // Input borders
    hover: "#eee",        // Light borders
    bg: "#f5f5f2",        // Card/image backgrounds
    bgLight: "#f2f2f2",   // Footer bottom bar
    white: "#ffffff",
  },
  // Status / semantic
  status: {
    sale: "#c0392b",      // Sale badges, wishlist active
    success: "#27ae60",
    warning: "#f39c12",
    info: "#2980b9",
  },
} as const;

// ─── Typography ─────────────────────────────────────────
export const typography = {
  fontFamily: {
    primary: "'Instrument Sans', sans-serif",
  },
  fontImport:
    "https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap",
  heading: {
    h1: { size: "clamp(34px, 5vw, 56px)", weight: 500, lineHeight: 1.08 },
    h2: { size: "clamp(28px, 3.4vw, 40px)", weight: 500, lineHeight: 1.15 },
    h3: { size: "clamp(20px, 2.2vw, 30px)", weight: 500, lineHeight: 1.2 },
  },
  body: {
    size: "16px",
    lineHeight: 1.6,
    letterSpacing: "0",
  },
  eyebrow: {
    size: "13px",
    letterSpacing: "0.14em",
    transform: "uppercase" as const,
  },
} as const;

// ─── Layout ─────────────────────────────────────────────
export const layout = {
  maxWidth: "1680px",
  containerPadding: {
    desktop: "40px",
    tablet: "24px",
    mobile: "16px",
  },
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
  borderRadius: {
    card: "16px",    // rounded-2xl
    hero: "24px",    // rounded-3xl
    button: "999px", // full pill
    dropdown: "16px",
  },
} as const;

// ─── Animations ─────────────────────────────────────────
export const animations = {
  marqueeSpeed: 30,        // seconds for one full scroll cycle
  imageZoomScale: 1.06,
  imageZoomDuration: "0.8s",
  revealOffset: "24px",
  revealDuration: "0.7s",
  spinDuration: "14s",
} as const;

// ─── Navigation ─────────────────────────────────────────
export const navigation = {
  announcements: [
    "No Animal Testing",
    "Sign up & enjoy 10% off",
    "Free shipping on orders ₹499+",
  ],
  mainNav: [
    { label: "Home", href: "/" },
    {
      label: "Shop",
      href: "/collections/all",
      children: [
        { label: "Skincare", href: "/collections/skincare" },
        { label: "Makeup", href: "/collections/makeup" },
        { label: "Bodycare", href: "/collections/bodycare" },
        { label: "Haircare", href: "/collections/haircare" },
        { label: "Accessories", href: "/collections/accessories" },
      ],
    },
    { label: "New & Trending", href: "/collections/new-arrivals" },
    { label: "Ritual Finder", href: "/quiz" },
    { label: "Build a Box", href: "/build-a-box" },
    { label: "Blog", href: "/blog" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  footerMenus: [
    {
      heading: "Quick Links",
      links: [
        { label: "New Arrivals", href: "/collections/new-arrivals" },
        { label: "Best Sellers", href: "/collections/all" },
        { label: "Skincare", href: "/collections/skincare" },
        { label: "Makeup", href: "/collections/makeup" },
        { label: "Build a Box (Save 20%)", href: "/build-a-box" },
      ],
    },
    {
      heading: "Company",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Ingredient Lab", href: "/ingredients" },
        { label: "Our Story", href: "/about" },
        { label: "Sustainability", href: "/about" },
        { label: "Careers", href: "/about" },
        { label: "Press", href: "/about" },
      ],
    },
    {
      heading: "Customer Care",
      links: [
        { label: "Contact Us", href: "/contact" },
        { label: "Shipping Policy", href: "/shipping-policy" },
        { label: "Returns & Refunds", href: "/refund-policy" },
        { label: "Terms of Service", href: "/terms-of-service" },
        { label: "Privacy Policy", href: "/privacy-policy" },
        { label: "Track Order", href: "/account" },
      ],
    },
  ],
  socialLinks: [
    { platform: "instagram", url: "#" },
    { platform: "facebook", url: "#" },
    { platform: "youtube", url: "#" },
    { platform: "twitter", url: "#" },
  ],
  paymentMethods: ["Visa", "MC", "RuPay", "UPI", "Razorpay", "GPay"],
} as const;

// ─── Homepage Content (used until WooCommerce sourcing) ─
// This mock data keeps the UI working before backend integration
export const CDN = "https://cdn.shopify.com/s/files/1/0639/8853/1298/files/";
export const img = (name: string) => `${CDN}${name}`;

export const homepageContent = {
  heroCards: [
    {
      image: img("image-card-03.jpg"),
      subheading: "Hydra Boost",
      heading: "Natural Beauty Ritual",
      desc: "Upgrade your routine with a clean, minimalist collection that delivers real results, naturally.",
      button: "Explore Now",
      href: "/collections/all",
    },
    { image: img("image-card-04.jpg") },
  ],
  marqueeTagline:
    "Refresh your daily ritual with clean, plant-powered skincare that nourishes, protects, and restores.",
  categoryHeadline:
    "From glow-boosting skincare to everyday beauty must-haves — shop by category to find your favourites.",
  categoryList: [
    { title: "Modern Beauty Picks", image: img("collection-04.jpg"), href: "/collections/makeup" },
    { title: "SkinCare", image: img("collection-03.jpg"), href: "/collections/skincare" },
    { title: "BodyCare Ritual", image: img("collection-02.jpg"), href: "/collections/bodycare" },
    { title: "HairCare", image: img("collection-01.jpg"), href: "/collections/haircare" },
  ],
  shopTheLook: {
    heading:
      "Shop the look you love – everything you need to recreate this effortless, glowing routine from start to finish",
    items: [
      { title: "Healthy Hair Ritual", image: img("collection-05.jpg"), href: "/collections/haircare" },
      { title: "Rosy Touch", image: img("collection-08.jpg"), href: "/collections/makeup" },
      { title: "Daily Glow Routine", image: img("collection-06.jpg"), href: "/collections/skincare" },
      { title: "Glow & Go Set", image: img("collection-07.jpg"), href: "/collections/all" },
      { title: "Hydra Boost Serum", image: img("collection-09.jpg"), href: "/collections/skincare" },
    ],
  },
  ritualList: [
    { title: "Calm Repair Oil", image: img("collection-14.jpg"), href: "/collections/bodycare" },
    { title: "Botanical Beauty Kit", image: img("collection-10.jpg"), href: "/collections/all" },
    { title: "Everyday Natural Look", image: img("collection-11.jpg"), href: "/collections/makeup" },
    { title: "Soft Skin Cream", image: img("collection-13.jpg"), href: "/collections/bodycare" },
    { title: "Pure Skin Essentials", image: img("collection-12.jpg"), href: "/collections/skincare" },
  ],
  press: [
    { logo: img("client-logo-01.png"), text: "\u201CA refreshing take on clean beauty — minimal, effective, and beautifully crafted.\u201D" },
    { logo: img("client-logo-02.png"), text: "\u201CMillions of combinations give you a truly unique routine made exactly the way you want.\u201D" },
    { logo: img("client-logo-06.png"), text: "\u201CLightweight, gentle and effective — better than anything else in my cabinet.\u201D" },
    { logo: img("client-logo-05.png"), text: "\u201CAmazing product. Transformed my skin, leaving my face plump and healthy. Highly recommend!\u201D" },
    { logo: img("client-logo-04.png"), text: "\u201CGreat formulas — my skin adjusted quickly. Nothing like the old products; clean with real results.\u201D" },
    { logo: img("client-logo-03.png"), text: "\u201CAmazing product. The texture is transformative, and my skin feels plump and healthy.\u201D" },
  ],
  collectionTabs: {
    subheading: "featured collections",
    tabs: [
      { title: "Green Reset", image: img("collection-tab-06.jpg"), desc: "A gentle reset for stressed skin using clean botanical care", href: "/collections/skincare" },
      { title: "Glow & Go Set", image: img("collection-tab-05.jpg"), desc: "On-the-go beauty essentials for fast glow and confidence", href: "/collections/all" },
      { title: "Pure Glow", image: img("collection-tab-04.jpg"), desc: "Clean skincare that reveals natural radiance and healthy skin glow", href: "/collections/skincare" },
      { title: "Hydra Ritual", image: img("collection-tab-03.jpg"), desc: "A daily hydration ritual for plump, dewy, and balanced skin", href: "/collections/skincare" },
      { title: "Coconut Calm", image: img("collection-tab-02.jpg"), desc: "Soothing coconut formulas for soft, hydrated, stress-free skin", href: "/collections/bodycare" },
    ],
  },
  productTabs: {
    heading: "Curated for you",
    tabs: [
      { key: "skincare", label: "Skincare" },
      { key: "makeup", label: "Makeup" },
      { key: "bodycare", label: "Bodycare" },
      { key: "accessories", label: "Accessories" },
    ],
    cards: [
      { image: img("image-card-16.jpg"), subheading: "Nourish & Renew", heading: "Pure Glow Ritual", button: "Shop Now", href: "/collections/skincare" },
      { image: img("image-card-15.jpg"), subheading: "Fresh Dew Routine", heading: "Gilead Repair Oil", button: "Shop Now", href: "/collections/bodycare" },
      { image: img("image-card-17.jpg"), subheading: "Crystal Dust", heading: "Glow Above All", button: "Shop Now", href: "/collections/all" },
    ],
  },
  countdown: {
    subheading: "Limited time only",
    heading: "Flash beauty deals you'll love",
    button: "Shop All",
    href: "/collections/all",
    endsInDays: 14,
    products: ["hydra-boost-serum", "glow-renewal-serum", "squalane-recovery-oil"],
  },
  logoBadge: {
    badge: img("img-layer-01.png"),
  },
  featureSection: {
    image: img("featured-image-01.png"),
    heading:
      "Refresh your daily ritual with clean, plant-powered skincare that nourishes, protects, and restores.",
    features: [
      { icon: img("icon-01.svg"), title: "Clean Ingredients", desc: "Formulated without parabens, sulfates, or synthetic fragrances — just pure goodness." },
      { icon: img("icon-02.svg"), title: "Plant-Based Formulas", desc: "Powered by botanical extracts and essential oils for real, gentle results." },
      { icon: img("icon-03.svg"), title: "Gentle on Skin", desc: "Perfect for all skin types, especially sensitive; soothes and protects without irritation." },
      { icon: img("icon-04.svg"), title: "Eco-Friendly Beauty", desc: "Cruelty-free, sustainably packaged, thoughtfully caring for your skin and planet." },
    ],
  },
  dualBanners: [
    {
      image: img("image-card-05.jpg"),
      subheading: "United for Better Skin",
      heading: "Pure Glow Ritual",
      desc: "Upgrade your routine with a clean, minimalist collection that delivers real results, naturally.",
      button: "Explore Now",
      href: "/collections/skincare",
      layout: "center" as const,
    },
    {
      image: img("image-card-06.jpg"),
      subheading: "Glow Up Pack",
      heading: "Fresh Face Routine",
      button: "Explore Now",
      href: "/collections/all",
      layout: "bottom" as const,
    },
  ],
  brandMarquee: [
    { text: "@meoora", italic: true },
    { text: "100% Sensorial, 100% Natural" },
    { text: "@meoora", italic: true },
    { text: "United for better skin" },
    { text: "@meoora", italic: true },
    { text: "The art of natural beauty" },
  ],
  testimonials: {
    heading: "Loved by real customers",
    items: [
      { name: "Rohan V.", stars: 5, title: "Exceptional quality & fast absorption!", quote: "Absorbs beautifully without any synthetic fragrance or residue. 10/10 recommend for sensitive skin.", product: "hydra-boost-serum" },
      { name: "Pooja M.", stars: 5, title: "Obsessed with this clean formula!", quote: "The texture is featherlight and my skin adapted within days. Truly clean results without any breakouts.", product: "glow-renewal-serum" },
      { name: "Aryan S.", stars: 5, title: "Revolutionized my daily ritual!", quote: "This formulation completely revolutionized my daily skincare routine. Lightweight yet deeply replenishing.", product: "squalane-recovery-oil" },
      { name: "Ananya K.", stars: 5, title: "Calming and so balancing!", quote: "Gentle on reactive skin, keeps my T-zone balanced throughout the day. Love the natural floral aroma.", product: "balance-toner-niacinamide" },
      { name: "Dr. Meera N.", stars: 5, title: "Remarkable botanical purity!", quote: "Deeply hydrates the skin barrier without feeling heavy or greasy. Works wonderfully under makeup.", product: "deep-hydration-barrier-cream" },
    ],
  },
  shopTheFeed: {
    heading: "See the beauty in motion — real textures, real skin, and real results.",
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
    items: [
      { product: "vegan-mascara", cover: img("collection-04.jpg") },
      { product: "cooling-mint-clay-hair-mask", cover: img("collection-05.jpg") },
      { product: "argan-hair-oil-elixir", cover: img("collection-01.jpg") },
      { product: "eyeshadow-palette", cover: img("collection-08.jpg") },
      { product: "gentle-gel-cleanser", cover: img("collection-06.jpg") },
      { product: "hydra-boost-serum", cover: img("collection-09.jpg") },
    ],
  },
  faqs: {
    subheading: "Can't find your answer?",
    heading: "Frequently asked questions",
    items: [
      { q: "How long does shipping take?", a: "Orders are processed within 1–2 business days. Standard delivery takes 3–5 business days, while express options arrive in 1–2 business days." },
      { q: "Does my piece come in any packaging?", a: "Yes, each product is carefully packed in sturdy, eco-friendly packaging. This packaging not only protects the item but also preserves its quality and provides an enjoyable unboxing experience upon arrival." },
      { q: "Do you ship internationally?", a: "Yes, we ship internationally to many countries worldwide. Delivery times may vary by location, but we always ensure your order is carefully packed and arrives safely." },
      { q: "What payment methods do you accept?", a: "We accept various payment methods, including credit and debit cards, as well as popular digital wallets like UPI, Razorpay, RuPay, and Google Pay — making checkout fast and secure." },
      { q: "What is your return policy?", a: "We accept returns or exchanges within 14 days of receiving your order. Items must be unused, in their original packaging, and in perfect condition to qualify for a refund or replacement." },
    ],
  },
  imageBoxes: [
    { image: img("image-box-01.jpg"), heading: "Beauty talk & tips", desc: "Your go-to space for curated skincare tips, clean beauty insights, and trusted reviews — all in one place.", href: "/blog" },
    { image: img("image-box-02.jpg"), heading: "Botanical Concierge", desc: "Consult directly with our skincare specialists for customized rituals and clean ingredient guidance.", href: "/contact" },
  ],
} as const;

// ─── Products Catalog & Type Definitions ──────────────────
export interface ProductVariation {
  id: number | string;
  sku: string;
  price: number;
  regularPrice: number;
  compareAt?: number | null;
  stockStatus: string;
  stockQuantity?: number | null;
  attributes: Record<string, string>;
  image?: string;
}

export interface ProductAttribute {
  name: string;
  slug: string;
  options: string[];
  variation?: boolean;
}

export interface ProductGalleryMedia {
  type: "image" | "video";
  url: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
}

export interface ProductFaq {
  question: string;
  answer: string;
}

export interface ProductKeyActive {
  title: string;
  source: string;
  benefit: string;
  icon?: string;
}

export interface ProductRitualStep {
  step: number;
  title: string;
  instruction: string;
}

export interface ProductSpecifications {
  skinType?: string;
  texture?: string;
  aroma?: string;
  ph?: string;
  volume?: string;
}

export interface MockProduct {
  id: string | number;
  databaseId?: number;
  handle: string;
  title: string;
  price: number;
  regularPrice?: number;
  compareAt: number | null;
  image: string;
  image2: string;
  images?: string[];
  galleryMedia?: ProductGalleryMedia[];
  videoUrl?: string | null;
  faqs?: ProductFaq[];
  keyActives?: ProductKeyActive[];
  ritualSteps?: ProductRitualStep[];
  specifications?: ProductSpecifications;
  specs?: ProductSpecifications;
  inci?: string;
  freeFrom?: string[];
  category: string;
  categories?: Array<{ id?: number | string; name?: string; slug: string }>;
  tag?: string;
  rating: number;
  reviews: number;
  averageRating?: number;
  reviewCount?: number;
  vendor: string;
  type?: "simple" | "variable";
  sku?: string;
  stockStatus?: string;
  stockQuantity?: number | null;
  shortDescription?: string;
  description: string;
  attributes?: ProductAttribute[];
  variations?: ProductVariation[];
}

export const mockProducts: MockProduct[] = [
  {
    id: 198,
    databaseId: 198,
    handle: "hydra-boost-serum",
    title: "Hydra-Boost Botanical Serum",
    type: "variable",
    price: 1499,
    regularPrice: 1799,
    compareAt: 1799,
    sku: "MEO-HBS-30",
    stockStatus: "instock",
    stockQuantity: 35,
    category: "skincare",
    tag: "Botanical Choice",
    rating: 4.8,
    reviews: 5,
    vendor: "Meoora",
    image: img("collection-09.jpg"),
    image2: img("collection-05.jpg"),
    images: [img("collection-09.jpg"), img("collection-05.jpg"), img("collection-12.jpg")],
    galleryMedia: [
      { type: "image", url: img("collection-09.jpg"), alt: "Hydra-Boost Botanical Serum 1:1" },
      { type: "image", url: img("collection-05.jpg"), alt: "Hydra-Boost Botanical Serum Texture" },
      { type: "image", url: img("collection-12.jpg"), alt: "Hydra-Boost Botanical Serum Lifestyle" },
      {
        type: "video",
        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
        thumbnail: img("collection-09.jpg"),
        title: "Hydra-Boost Botanical Ritual Video",
      },
    ],
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
    specs: {
      skinType: "Dehydrated, sensitive, dry, and combination skin types",
      texture: "Silky, featherweight quick-absorbing botanical elixir",
      aroma: "Cold-distilled Damask Rose & subtle green tea notes",
      ph: "5.2 – 5.5",
    },
    specifications: {
      skinType: "Dehydrated, sensitive, dry, and combination skin types",
      texture: "Silky, featherweight quick-absorbing botanical elixir",
      aroma: "Cold-distilled Damask Rose & subtle green tea notes",
      ph: "5.2 – 5.5",
    },
    keyActives: [
      {
        title: "Multi-Molecular Hyaluronic Acid",
        source: "Bio-fermented wheat germ",
        benefit: "Multi-depth dermal hydration that binds up to 1,000x its weight in water to visibly plump fine surface lines.",
      },
      {
        title: "Centella Asiatica (Gotu Kola)",
        source: "Wild-harvested Himalayan leaves",
        benefit: "Accelerates collagen synthesis, diminishes trans-epidermal water loss, and calms redness.",
      },
      {
        title: "Damask Rose Floral Hydrosol",
        source: "First-steam artisanal copper distillation",
        benefit: "Provides natural cellular hydration, delicate astringent toning, and antioxidant protection.",
      },
      {
        title: "Vegetable Glycerin & Betaine",
        source: "Sustainable organic coconut & sugar beet",
        benefit: "Maintains cellular osmotic balance and provides non-sticky humectant moisture retention.",
      },
    ],
    ritualSteps: [
      {
        step: 1,
        title: "Purify & Awaken",
        instruction: "Begin with a clean canvas. Mist skin with floral hydrosol or keep slightly damp to maximize molecular moisture absorption.",
      },
      {
        step: 2,
        title: "Dispense Active Elixir",
        instruction: "Release 3–4 drops into the palm of your hand. Gently warm between clean fingertips.",
      },
      {
        step: 3,
        title: "Press & Glide",
        instruction: "Press upward across cheeks, forehead, chin, and décolletage with light, sweeping motions until fully absorbed.",
      },
      {
        step: 4,
        title: "Seal & Protect",
        instruction: "Follow immediately with Deep Hydration Barrier Cream or Squalane Recovery Oil to lock in all bio-available actives.",
      },
    ],
    faqs: [
      {
        question: "Can I use the Hydra-Boost Botanical Serum both morning and evening?",
        answer: "Yes, absolutely. Our water-based formula is designed for daily twice-a-day use. In the morning it provides all-day plumpness under sunscreen, and at night it recharges dehydrated skin while you sleep.",
      },
      {
        question: "How does this layer with Vitamin C or Niacinamide?",
        answer: "Hydra-Boost layers seamlessly with other active serums. We recommend applying thinner, water-based serums first (like our Glow Renewal Serum or Vitamin C), followed by Hydra-Boost, and finishing with a barrier moisturizer.",
      },
      {
        question: "Is this safe for acne-prone or reactive skin?",
        answer: "Yes. It is non-comedogenic, oil-free, silicone-free, and formulated at skin-friendly pH 5.3. Organic Gotu Kola actively soothes inflammatory flare-ups without clogging pores.",
      },
      {
        question: "What is the shelf life once opened?",
        answer: "Because we preserve our botanical elixirs with clean radish root ferment and lactobacillus, we recommend using within 9 months of opening. Store away from direct sunlight.",
      },
    ],
    inci: "Aqua (Water), Rosa Damascena (Rose) Flower Water*, Sodium Hyaluronate (Multi-Molecular Hyaluronic Acid), Centella Asiatica (Gotu Kola) Leaf Extract*, Glycerin, Betaine, Hydrolyzed Sodium Hyaluronate, Camellia Sinensis (Green Tea) Leaf Extract*, Leuconostoc/Radish Root Ferment Filtrate, Citric Acid, Potassium Sorbate.",
    freeFrom: ["Parabens", "Sulfates", "Phthalates", "Silicones", "Synthetic Dyes", "Artificial Fragrances", "Mineral Oil"],
    shortDescription: "Intensive moisture infusion formulated with multi-molecular hyaluronic acid, gotu kola, and fresh damask rose water.",
    description: `<p class="lead">An elixir of deep biological hydration. Our Hydra-Boost Botanical Serum restores moisture balance across all dermal layers, leaving skin plump, supple, and lit from within.</p><h3>Botanical Science</h3><p>Combines three weights of plant-derived Hyaluronic Acid with organic Gotu Kola extract (Centella Asiatica) to accelerate natural skin barrier repair. Cold-distilled Damask Rose water provides gentle anti-inflammatory toning.</p><h3>How to Use</h3><p>Gently press 3–4 drops onto cleansed, damp skin morning and night before heavier creams.</p>`,
    attributes: [
      { name: "Volume", slug: "volume", options: ["30ml", "50ml", "100ml"], variation: true },
    ],
    variations: [
      {
        id: 201,
        sku: "MEO-HBS-30",
        price: 1499,
        regularPrice: 1799,
        compareAt: 1799,
        stockStatus: "instock",
        stockQuantity: 35,
        attributes: { volume: "30ml" },
        image: img("collection-09.jpg"),
      },
      {
        id: 203,
        sku: "MEO-HBS-50",
        price: 2099,
        regularPrice: 2499,
        compareAt: 2499,
        stockStatus: "instock",
        stockQuantity: 20,
        attributes: { volume: "50ml" },
        image: img("collection-09.jpg"),
      },
      {
        id: 205,
        sku: "MEO-HBS-100",
        price: 3299,
        regularPrice: 3999,
        compareAt: 3999,
        stockStatus: "instock",
        stockQuantity: 8,
        attributes: { volume: "100ml" },
        image: img("collection-09.jpg"),
      },
    ],
  },
  {
    id: 211,
    databaseId: 211,
    handle: "glow-renewal-serum",
    title: "Glow Renewal Niacinamide Serum",
    type: "variable",
    price: 1349,
    regularPrice: 1599,
    compareAt: 1599,
    sku: "MEO-GRS-30",
    stockStatus: "instock",
    stockQuantity: 28,
    category: "skincare",
    tag: "Bestseller",
    rating: 4.9,
    reviews: 4,
    vendor: "Meoora",
    image: img("collection-08.jpg"),
    image2: img("collection-12.jpg"),
    images: [img("collection-08.jpg"), img("collection-12.jpg")],
    shortDescription: "Clarifying antioxidant serum with 10% pure Niacinamide and wild Licorice root to refine pores and fade hyperpigmentation.",
    description: `<p class="lead">An illuminating daily formulation crafted to restore skin clarity, minimize visible pore diameter, and unify uneven pigmentation.</p><h3>Active Bio-Complex</h3><p>Powered by 10% clinical-grade Niacinamide (Vitamin B3) buffered with Zinc PCA and organic Licorice root extract. Shields the cellular matrix against urban smog and oxidative damage.</p><h3>Application</h3><p>Smooth 2–3 drops onto clean face prior to facial oils or moisturizers.</p>`,
    attributes: [
      { name: "Volume", slug: "volume", options: ["30ml", "50ml"], variation: true },
    ],
    variations: [
      {
        id: 213,
        sku: "MEO-GRS-30",
        price: 1349,
        regularPrice: 1599,
        compareAt: 1599,
        stockStatus: "instock",
        stockQuantity: 28,
        attributes: { volume: "30ml" },
        image: img("collection-08.jpg"),
      },
      {
        id: 215,
        sku: "MEO-GRS-50",
        price: 1849,
        regularPrice: 2199,
        compareAt: 2199,
        stockStatus: "instock",
        stockQuantity: 14,
        attributes: { volume: "50ml" },
        image: img("collection-08.jpg"),
      },
    ],
  },
  {
    id: 221,
    databaseId: 221,
    handle: "deep-hydration-barrier-cream",
    title: "Deep Hydration Barrier Cream",
    type: "variable",
    price: 1299,
    regularPrice: 1499,
    compareAt: 1499,
    sku: "MEO-DHB-50",
    stockStatus: "instock",
    stockQuantity: 40,
    category: "skincare",
    tag: "Ceramide Rich",
    rating: 4.8,
    reviews: 6,
    vendor: "Meoora",
    image: img("collection-12.jpg"),
    image2: img("product-countdown-01.jpg"),
    images: [img("collection-12.jpg"), img("product-countdown-01.jpg")],
    shortDescription: "Rich lipid-replenishing moisture balm formulated with cold-pressed ceramides, squalane, and organic oat extract.",
    description: `<p class="lead">Reinforce compromised moisture barriers with our biocompatible lipid emulsion. Melts on contact to deliver 72-hour continuous hydration without clogging pores.</p><h3>Clean Actives</h3><p>Contains phytosterols, Ceramide NP, Squalane, and Colloidal Oat Extract to pacify redness, tightness, and environmental sensitization.</p><h3>Application</h3><p>Warm a pea-sized amount between palms and massage into face and neck.</p>`,
    attributes: [
      { name: "Size", slug: "size", options: ["50g", "100g"], variation: true },
    ],
    variations: [
      {
        id: 223,
        sku: "MEO-DHB-50",
        price: 1299,
        regularPrice: 1499,
        compareAt: 1499,
        stockStatus: "instock",
        stockQuantity: 40,
        attributes: { size: "50g" },
        image: img("collection-12.jpg"),
      },
      {
        id: 225,
        sku: "MEO-DHB-100",
        price: 2099,
        regularPrice: 2499,
        compareAt: 2499,
        stockStatus: "instock",
        stockQuantity: 18,
        attributes: { size: "100g" },
        image: img("collection-12.jpg"),
      },
    ],
  },
  {
    id: 231,
    databaseId: 231,
    handle: "gentle-gel-cleanser",
    title: "Gentle Botanical Gel Cleanser",
    type: "simple",
    price: 749,
    regularPrice: 899,
    compareAt: 899,
    sku: "MEO-GGC-200",
    stockStatus: "instock",
    stockQuantity: 50,
    category: "skincare",
    tag: "Daily Essential",
    rating: 4.9,
    reviews: 8,
    vendor: "Meoora",
    image: img("collection-13.jpg"),
    image2: img("collection-06.jpg"),
    images: [img("collection-13.jpg"), img("collection-06.jpg")],
    shortDescription: "Sulfate-free foaming gel cleanser with green tea, cucumber distillate, and calendula for clean, refreshed skin.",
    description: `<p class="lead">Gently wash away makeup, sunscreen, and daily pollutants without disrupting your skin's vital acid mantle.</p><h3>Botanical Cleanse</h3><p>Formulated with coconut-derived amphoteric cleansing agents, wild harvested Green Tea extract, and organic Calendula petals. Leaves skin pH-balanced and soft.</p><h3>How to Use</h3><p>Pump onto wet palms, lather into a silky foam, massage over damp face for 60 seconds, and rinse with lukewarm water.</p>`,
  },
  {
    id: 237,
    databaseId: 237,
    handle: "balance-toner-niacinamide",
    title: "Balance Clarifying Floral Toner",
    type: "simple",
    price: 849,
    regularPrice: 999,
    compareAt: 999,
    sku: "MEO-BT-150",
    stockStatus: "instock",
    stockQuantity: 45,
    category: "skincare",
    tag: "Clarifying",
    rating: 4.7,
    reviews: 5,
    vendor: "Meoora",
    image: img("collection-03.jpg"),
    image2: img("collection-11.jpg"),
    images: [img("collection-03.jpg"), img("collection-11.jpg")],
    shortDescription: "Alcohol-free floral mist infused with witch hazel, rosemary hydrosol, and zinc for balanced, shine-free complexion.",
    description: `<p class="lead">Clarify, balance, and re-mineralize. An alcohol-free botanical toner formulated with pure artisanal hydrosols.</p><h3>Botanical Science</h3><p>Combines distillates of Witch Hazel, Rosemary, and Chamomile with Zinc Gluconate to tighten visible pores and remove residual impurities.</p><h3>How to Use</h3><p>Mist directly onto face or sweep gently across skin using an organic cotton round.</p>`,
  },
  {
    id: 243,
    databaseId: 243,
    handle: "squalane-recovery-oil",
    title: "Pure Olive Squalane Recovery Oil",
    type: "variable",
    price: 999,
    regularPrice: 1199,
    compareAt: 1199,
    sku: "MEO-SRO-15",
    stockStatus: "instock",
    stockQuantity: 32,
    category: "skincare",
    tag: "100% Plant Derived",
    rating: 4.9,
    reviews: 6,
    vendor: "Meoora",
    image: img("collection-14.jpg"),
    image2: img("collection-10.jpg"),
    images: [img("collection-14.jpg"), img("collection-10.jpg")],
    shortDescription: "Featherlight, 100% plant-derived squalane extracted from sustainable olives to lock in moisture and impart instant radiance.",
    description: `<p class="lead">A biomimetic oil that mimics skin's natural sebum. Instantly absorbs to seal in hydration, calm irritation, and impart a glass-like glow without feeling greasy.</p><h3>Pure Olive Extraction</h3><p>100% pure, single-origin Squalane derived from renewable olive groves. Non-comedogenic and suitable for the most sensitive or acne-prone skin types.</p><h3>How to Use</h3><p>Press 2–3 drops as the final step of your nighttime skincare ritual or blend a drop with your favorite makeup foundation.</p>`,
    attributes: [
      { name: "Volume", slug: "volume", options: ["15ml", "30ml"], variation: true },
    ],
    variations: [
      {
        id: 245,
        sku: "MEO-SRO-15",
        price: 999,
        regularPrice: 1199,
        compareAt: 1199,
        stockStatus: "instock",
        stockQuantity: 32,
        attributes: { volume: "15ml" },
        image: img("collection-14.jpg"),
      },
      {
        id: 247,
        sku: "MEO-SRO-30",
        price: 1599,
        regularPrice: 1899,
        compareAt: 1899,
        stockStatus: "instock",
        stockQuantity: 16,
        attributes: { volume: "30ml" },
        image: img("collection-14.jpg"),
      },
    ],
  },
  {
    id: 253,
    databaseId: 253,
    handle: "dewy-lip-cheek-tint",
    title: "Dewy Plant-Pigment Lip & Cheek Tint",
    type: "variable",
    price: 599,
    regularPrice: 699,
    compareAt: 699,
    sku: "MEO-DCT-PR",
    stockStatus: "instock",
    stockQuantity: 60,
    category: "makeup",
    tag: "Multi-Use Tint",
    rating: 4.8,
    reviews: 7,
    vendor: "Meoora",
    image: img("collection-08.jpg"),
    image2: img("collection-04.jpg"),
    images: [img("collection-08.jpg"), img("collection-04.jpg")],
    shortDescription: "Nourishing multi-use balm loaded with beetroot and hibiscus pigments for a natural, buildable flush of color.",
    description: `<p class="lead">A dewy multi-tasking pot of sheer botanical color. Formulated with organic shea butter and plant waxes to nourish lips and cheeks effortlessly.</p><h3>Clean Pigment Technology</h3><p>Tinted exclusively with mineral oxides, organic Beetroot extract, and dried Hibiscus flowers. No synthetic dyes or carmine.</p><h3>How to Use</h3><p>Dab with clean fingertips or brush onto apples of cheeks and lips for a fresh, healthy flush.</p>`,
    attributes: [
      { name: "Shade", slug: "shade", options: ["Petal Rose", "Warm Honey", "Wild Berry"], variation: true },
    ],
    variations: [
      {
        id: 255,
        sku: "MEO-DCT-PR",
        price: 599,
        regularPrice: 699,
        compareAt: 699,
        stockStatus: "instock",
        stockQuantity: 60,
        attributes: { shade: "Petal Rose" },
        image: img("collection-08.jpg"),
      },
      {
        id: 257,
        sku: "MEO-DCT-WH",
        price: 599,
        regularPrice: 699,
        compareAt: 699,
        stockStatus: "instock",
        stockQuantity: 45,
        attributes: { shade: "Warm Honey" },
        image: img("collection-08.jpg"),
      },
      {
        id: 259,
        sku: "MEO-DCT-WB",
        price: 599,
        regularPrice: 699,
        compareAt: 699,
        stockStatus: "instock",
        stockQuantity: 30,
        attributes: { shade: "Wild Berry" },
        image: img("collection-08.jpg"),
      },
    ],
  },
  {
    id: 265,
    databaseId: 265,
    handle: "argan-hair-oil-elixir",
    title: "Moroccan Argan Hair Treatment Elixir",
    type: "simple",
    price: 999,
    regularPrice: 1199,
    compareAt: 1199,
    sku: "MEO-AHO-100",
    stockStatus: "instock",
    stockQuantity: 25,
    category: "haircare",
    tag: "Restorative",
    rating: 4.9,
    reviews: 5,
    vendor: "Meoora",
    image: img("product-countdown-02.jpg"),
    image2: img("collection-01.jpg"),
    images: [img("product-countdown-02.jpg"), img("collection-01.jpg")],
    shortDescription: "Cold-pressed pure Moroccan Argan oil enriched with Rosemary and Vitamin E to tame frizz and nourish split ends.",
    description: `<p class="lead">An opulent elixir for lustrous, glossy hair. Restores vitality to heat-damaged strands while calming itchy scalps.</p><h3>Authentic Moroccan Sourcing</h3><p>100% virgin cold-pressed Argan oil sourced directly from women-led cooperatives in Morocco. Infused with rosemary essential oil for follicle stimulation.</p><h3>How to Use</h3><p>Warm 2 drops in hands and run through mid-lengths to ends of damp hair, or use as an overnight scalp oil ritual.</p>`,
  },
  {
    id: "self-tanning-body",
    handle: "self-tanning-body",
    title: "Botanical Illuminating Body Lotion",
    type: "simple",
    price: 1199,
    compareAt: 1499,
    image: img("collection-02.jpg"),
    image2: img("collection-11.jpg"),
    category: "bodycare",
    rating: 4.8,
    reviews: 29,
    vendor: "Meoora",
    description: "Gradual sun-kissed glow lotion packed with DHA from sugar beets, coconut oil, and shea butter.",
  },
  {
    id: "vegan-mascara",
    handle: "vegan-mascara",
    title: "Vegan Volume Botanical Mascara",
    type: "simple",
    price: 799,
    compareAt: null,
    image: img("collection-11.jpg"),
    image2: img("collection-04.jpg"),
    category: "makeup",
    tag: "New",
    rating: 4.9,
    reviews: 64,
    vendor: "Meoora",
    description: "Deep black mineral mascara infused with castor oil and berry waxes for voluminous, lengthened lashes.",
  },
  {
    id: "eyeshadow-palette",
    handle: "eyeshadow-palette",
    title: "Botanical Earth Eyeshadow Palette",
    type: "simple",
    price: 1399,
    compareAt: 1799,
    image: img("collection-04.jpg"),
    image2: img("collection-08.jpg"),
    category: "makeup",
    rating: 4.8,
    reviews: 48,
    vendor: "Meoora",
    description: "9 velvety buttery mineral pans inspired by earthy botanical pigments, petals, and forest bark.",
  },
  {
    id: "cooling-mint-clay-hair-mask",
    handle: "cooling-mint-clay-hair-mask",
    title: "Cooling Mint Clay Hair Scalp Mask",
    type: "simple",
    price: 1299,
    compareAt: null,
    image: img("collection-05.jpg"),
    image2: img("collection-09.jpg"),
    category: "haircare",
    rating: 4.7,
    reviews: 31,
    vendor: "Meoora",
    description: "Detoxifying French green clay and peppermint oil treatment to invigorate scalp microcirculation.",
  },
  {
    id: "crystal-dust-highlighter",
    handle: "crystal-dust-highlighter",
    title: "Crystal Dust Mineral Highlighter",
    type: "simple",
    price: 899,
    compareAt: 1099,
    image: img("collection-07.jpg"),
    image2: img("collection-08.jpg"),
    category: "accessories",
    rating: 4.9,
    reviews: 73,
    vendor: "Meoora",
    description: "Prismatic mica highlighter dust that catches sunlight for an ethereal glass skin finish.",
  },
];

export const getProduct = (handle: string) => mockProducts.find((p) => p.handle === handle);
export const byCategory = (cat: string) =>
  cat === "all" || cat === "new-arrivals"
    ? mockProducts
    : mockProducts.filter((p) => p.category === cat);

// ─── Blog Articles & Editorial Journal ───────────────────
export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: {
    name: string;
    role: string;
    avatar?: string;
  };
  category: string;
  readingTime: string;
  image: string;
  tags: string[];
}

export const blogArticles: BlogArticle[] = [
  {
    id: "clean-beauty-morning-routine",
    slug: "clean-beauty-morning-routine",
    title: "The 10-Step Clean Beauty Routine for Radiant Morning Skin",
    excerpt: "Discover how botanical cleansers, active toners, and plant squalane work synergistically to protect your skin barrier against daily pollution and oxidative stress.",
    date: "Sep 10, 2026",
    author: {
      name: "Dr. Ananya Rao",
      role: "Holistic Dermatologist & Botanical Chemist",
    },
    category: "Skincare Guides",
    readingTime: "5 min read",
    image: img("image-box-01.jpg"),
    tags: ["Skincare", "Morning Routine", "Clean Beauty", "Barrier Health"],
    content: `
      <p class="lead">Achieving that coveted lit-from-within morning glow is not about layering dozens of synthetic products. It is about honoring your skin's natural rhythm with botanical formulations designed to support, not compromise, the lipid barrier.</p>
      
      <h2>1. Gentle Awakening with a Gel Cleanser</h2>
      <p>Overnight, the skin eliminates cellular metabolic waste while regenerating. Rather than stripping vital sebum with harsh sulfates, reach for a mild, coconut-derived gel cleanser infused with cucumber distillate and chamomile. This clears surface impurities while preserving the delicate acid mantle.</p>
      
      <h2>2. Restoring Hydration with Botanical Distillates</h2>
      <p>Tap water often contains high mineral content that alters skin pH. Patting in a soothing rosewater and niacinamide toner rebalances equilibrium, tightens the appearance of pores, and prepares the stratum corneum to absorb fat-soluble actives deeply.</p>
      
      <h2>3. The Serum Step: Plant Squalane & Hyaluronic Synergy</h2>
      <p>Layering a botanical serum containing multi-molecular weight hyaluronic acid draws atmospheric moisture into the epidermal layers. Follow immediately with lightweight plant-derived squalane from olives to seal this hydration within cellular matrices.</p>
      
      <blockquote>"True radiance stems from barrier integrity. When your lipid layer is replenished with bio-compatible botanical oils, light reflects evenly across the skin surface." — Dr. Ananya Rao</blockquote>
      
      <h2>4. Lock & Protect</h2>
      <p>Conclude your morning ritual with a lightweight moisturizer rich in ceramides, followed by broad-spectrum mineral SPF. Your complexion remains plump, resilient, and luminous from dawn until dusk.</p>
    `,
  },
  {
    id: "botanical-squalane-vs-hyaluronic-acid",
    slug: "botanical-squalane-vs-hyaluronic-acid",
    title: "Squalane vs Hyaluronic Acid: Which Hydrator Does Your Skin Need?",
    excerpt: "Understanding the crucial difference between humectants and emollients is the secret to unlocking lasting hydration without clogging pores.",
    date: "Aug 28, 2026",
    author: {
      name: "Priya Sundaram",
      role: "Formulation Scientist",
    },
    category: "Ingredients",
    readingTime: "6 min read",
    image: img("collection-12.jpg"),
    tags: ["Ingredients", "Hydration", "Squalane", "Hyaluronic Acid"],
    content: `
      <p class="lead">Hydration and moisture are often used interchangeably in modern skincare, but in cosmetic chemistry, they represent fundamentally distinct biological processes.</p>
      
      <h2>The Water Magnet: Hyaluronic Acid</h2>
      <p>Hyaluronic acid is a powerful humectant capable of holding up to 1,000 times its molecular weight in water. When applied to slightly damp skin, it pulls hydration into cellular compartments, plumping fine dehydration lines and creating a bouncy surface texture.</p>
      
      <h2>The Lipid Shield: Olive-Derived Squalane</h2>
      <p>Squalane is a hydrogenated, stable form of squalene — a lipid our sebaceous glands naturally produce. Because its molecular structure mimics natural sebum, olive squalane sinks in instantly without greasiness, acting as an emollient that locks the water attracted by hyaluronic acid deep within the dermis.</p>
      
      <h2>The Verdict: Combine for Optimal Results</h2>
      <p>Rather than choosing between the two, clean formulation science demonstrates that pairing a humectant followed by an emollient yields maximum cutaneous moisture retention throughout humid Indian summers and dry winter seasons alike.</p>
    `,
  },
  {
    id: "ayurvedic-herbs-modern-cosmetics",
    slug: "ayurvedic-herbs-modern-cosmetics",
    title: "5 Ancient Indian Herbs Transforming Modern Botanical Skincare",
    excerpt: "From cold-pressed Gotu Kola (Centella Asiatica) to organic Ashwagandha, explore how time-tested adaptogens soothe inflamed skin and defend against urban pollutants.",
    date: "Aug 15, 2026",
    author: {
      name: "Dr. Ananya Rao",
      role: "Holistic Dermatologist & Botanical Chemist",
    },
    category: "Ayurveda & Botanicals",
    readingTime: "7 min read",
    image: img("collection-01.jpg"),
    tags: ["Ayurveda", "Botanicals", "Herbal", "Adaptogens"],
    content: `
      <p class="lead">Centuries before synthetic peptides and lab-isolated retinoids emerged, ancient Ayurvedic texts documented the profound rejuvenating virtues of indigenous Indian botanicals.</p>
      
      <h2>1. Centella Asiatica (Gotu Kola / Brahmi)</h2>
      <p>Known as the herb of longevity, Gotu Kola is abundant in madecassoside and asiaticoside. In clinical trials, it accelerates micro-wound healing, suppresses oxidative inflammation, and stimulates type I collagen synthesis.</p>
      
      <h2>2. Ashwagandha (Indian Ginseng)</h2>
      <p>An adaptogenic root that moderates the impacts of environmental stress and cortisol on dermal cells. Topically applied, it revitalizes dull, exhausted complexions and reinforces antioxidant defenses.</p>
      
      <h2>3. Manjistha (Indian Madder)</h2>
      <p>Renowned for its blood-purifying and lymphatic stimulating properties, Manjistha gently fades post-inflammatory hyperpigmentation and creates an even, translucent tone.</p>
      
      <h2>4. Kumkumadi (Saffron Distillate)</h2>
      <p>Harvested from handpicked Kashmiri saffron stigmas, this precious botanical infusion imparts a golden luminescence while reducing free-radical damage from intense sun exposure.</p>
      
      <h2>5. Neem & Tulsi (Holy Basil)</h2>
      <p>The ultimate botanical purification duo. Their potent antibacterial and antifungal phytochemicals clarify blemish-prone skin without perturbing the beneficial commensal microbiome.</p>
    `,
  },
  {
    id: "nourishing-scalp-ritual-hair-oils",
    slug: "nourishing-scalp-ritual-hair-oils",
    title: "The Scalp-First Philosophy: Why Healthy Hair Begins with Plant Oils",
    excerpt: "How cold-pressed Argan, Bhringraj, and Rosemary oils stimulate microcirculation, reduce breakage, and create an optimal microclimate for thicker hair.",
    date: "Jul 30, 2026",
    author: {
      name: "Mira Kapoor",
      role: "Trichologist & Clean Hair Stylist",
    },
    category: "Haircare",
    readingTime: "4 min read",
    image: img("product-countdown-02.jpg"),
    tags: ["Haircare", "Scalp Care", "Argan Oil", "Rituals"],
    content: `
      <p class="lead">Treating dry hair ends without addressing the scalp follicle is like watering tree leaves while ignoring the roots. Modern trichology confirms what traditional hair rituals have taught for generations: vibrant hair starts at the scalp.</p>
      
      <h2>The Power of Cold-Pressed Plant Lipids</h2>
      <p>Virgin Moroccan Argan oil provides essential linoleic and oleic fatty acids that coat the hair shaft, preventing moisture loss during heat styling. When blended with botanical extracts of Bhringraj and Rosemary, the oil formulation actively encourages follicular blood flow.</p>
      
      <h2>The Warm Oil Champi Technique</h2>
      <p>Warm 10-15 drops between your palms. Section hair and massage gently in circular patterns starting from the nape of the neck toward the crown. Leave the treatment on for at least 45 minutes before rinsing with a sulfate-free cleansing cream.</p>
    `,
  },
  {
    id: "sustainable-packaging-pledge",
    slug: "sustainable-packaging-pledge",
    title: "Beyond the Bottle: Our Zero-Plastic & Recyclable Glass Commitment",
    excerpt: "A candid look into Meoora's supply chain, post-consumer recycled materials, and how we are pioneering compostable shipping in Indian e-commerce.",
    date: "Jul 12, 2026",
    author: {
      name: "Meoora Eco Team",
      role: "Sustainability & Operations",
    },
    category: "Sustainability",
    readingTime: "4 min read",
    image: img("image-box-02.jpg"),
    tags: ["Sustainability", "Eco-Friendly", "Zero-Waste", "Packaging"],
    content: `
      <p class="lead">At Meoora, clean beauty extends far beyond the ingredients inside our bottles. It defines how those bottles are crafted, packaged, and transported across India.</p>
      
      <h2>Amber Glass vs. Single-Use Plastic</h2>
      <p>We house our potent serums and oils in heavy flint amber glass. Glass is infinitely recyclable and provides natural UV shielding that preserves plant actives without synthetic preservatives.</p>
      
      <h2>Eco-Conscious Botanical Delivery</h2>
      <p>Every shipment sent through our logistics network uses FSC-certified recycled cardboard boxes, water-activated paper tape, and biodegradable cornstarch cushioning instead of plastic bubble wrap.</p>
    `,
  },
  {
    id: "makeup-skin-first-approach",
    slug: "makeup-skin-first-approach",
    title: "Skin-First Makeup: Creating Dewy, Minimal Looks with Plant Pigments",
    excerpt: "Why heavy foundations are out and breathable, serum-infused tints with antioxidant vitamins are in for effortless everyday confidence.",
    date: "Jun 24, 2026",
    author: {
      name: "Mira Kapoor",
      role: "Trichologist & Clean Hair Stylist",
    },
    category: "Makeup",
    readingTime: "5 min read",
    image: img("collection-04.jpg"),
    tags: ["Makeup", "Clean Beauty", "Dewy Glow", "Minimalism"],
    content: `
      <p class="lead">The era of cakey matte bases has given way to skin that looks like skin — fresh, hydrated, and breathing freely throughout long days.</p>
      
      <h2>Hybrids: Where Skincare Meets Color</h2>
      <p>By blending mineral iron oxides into lightweight jojoba and rosehip bases, our botanical tints unify tone while feeding your skin antioxidants throughout the day.</p>
      
      <h2>The 3-Minute Natural Flush</h2>
      <p>Dot a drop of Rosy Touch Lip & Cheek Stain onto the apples of cheeks and blend outward with fingertips. Finish with a sweep of Vegan Volume Mascara for clean, defined lashes.</p>
    `,
  },
];

export const getBlogArticle = (slug: string) =>
  blogArticles.find((a) => a.slug === slug || a.id === slug);

export const byBlogCategory = (cat: string) =>
  cat === "all"
    ? blogArticles
    : blogArticles.filter((a) => a.category.toLowerCase().includes(cat.toLowerCase()));
